import { getBooking } from "../../../app/api/getBooking";
import { BaseAction } from "../../../core/actions/BaseAction";
import { _eventBus } from "../../../core/events/EventBus";
import { getRuntimeConfig } from "../../../Lib/Config/RuntimeConfig";
import { STORAGE_KEYS } from "../../../Lib/Constants/storageKeys";
import { getTranslatedMessage } from "../../../Lib/Utils/i18n";
import { clearAllWhitelistedAtomsFromStorage, getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { Analytics } from "../../../Lib/Analytics/events";
import { extractBookingParams } from "../../../Lib/Analytics/bookingParams";
import { IPaymentGatewayCheckoutActionParams } from "../../../Orchestrator/Types/Interface/IPaymentProcess";
import { showNotification } from "../../../Services/notificationService";
import { redirectToPassBookingDetailPageAction } from "../CMSOffers/RedirectToPassBookingDetailPageAction";
import { requestInvoiceSubmitAction } from "../RequestInvoice/RequestInvoiceSubmitAction";

class PaymentGatewayCheckoutAction extends BaseAction<void, IPaymentGatewayCheckoutActionParams> {
    public async run(): Promise<void> {
        const { resultIndicator, accessToken } = this.params!;
        if (resultIndicator) {
            try {
                const config = await getRuntimeConfig();
                // Guests send neither Authorization nor Requestor; logged-in users do.
                const isAnonymous = getRecoilStateValue<boolean>("isAnonymousStateAtom") || false;
                fetch(
                    `/api/payment/resultindicator?resultIndicator=${resultIndicator}`,
                    {
                        headers: {
                            ...(!isAnonymous && { Authorization: `Bearer ${accessToken}` }),
                            "Content-Type": "application/json",
                            ...(!isAnonymous && { "Requestor": config.NEXT_PUBLIC_REQUESTOR })
                        },
                    }
                )
                    .then(async (res) => {
                        if (res.status === 409) {
                            // 409 is SUCCESS, not an error: beef already settled and
                            // fulfilled this session server-side. Its body carries no
                            // bookingId, so fall back to the id stashed before the MPGS
                            // hand-off — without it a guest lands on /bookings, which for
                            // an anonymous session is the retrieve-booking prompt rather
                            // than the booking they just paid for.
                            const data = await res.json().catch(() => ({}));
                            const bookingId = data.bookingId || readInFlightBookingId();
                            if (bookingId) {
                                await finalizePaymentSuccess(bookingId);
                            } else {
                                localStorage.removeItem(STORAGE_KEYS.CHANGE_DATE_FLOW);
                                navigateToBookings();
                                showPostPaymentFlowToasts();
                            }
                            return;
                        }
                        if (!res.ok) {
                            throw new Error("Payment verification failed");
                        }
                        const data = await res.json();
                        await finalizePaymentSuccess(data.bookingId);
                    })
                    .then(() => {
                    })
                    .catch((err) => {
                        console.log(("Error: " + err));
                    });
            } catch (err) {
                console.error(err);
            }
        }
    }
}

/**
 * The bookingId of the payment this browser handed off to MPGS.
 *
 * RedirectToPaymentGatewayAction stashes it immediately before opening the
 * lightbox (for every session, guest or logged-in), and sessionStorage survives
 * the redirect round-trip in the same tab. It therefore doubles as proof that a
 * real payment was started HERE — the beef redirect flow's result page carries
 * the outcome only in the query string, so without this marker a crafted
 * `?status=FULFILLED` link would be indistinguishable from a real return.
 *
 */
export function readInFlightBookingId(): string {
    try {
        return sessionStorage.getItem("guestCheckoutBookingId") || "";
    } catch {
        return "";
    }
}

/**
 * Consume the manage-booking flow flags and show their confirmation toasts.
 * Deferred so the destination page is mounted by the time they fire.
 */
export function showPostPaymentFlowToasts(): void {
    setTimeout(() => {
        if (localStorage.getItem("isUpgradeToPremiumFlow") == "true") {
            showNotification("success", getTranslatedMessage("ticketUpgradedTitle"), getTranslatedMessage("ticketUpgradedDesc"), 3, "success");
            localStorage.removeItem("isUpgradeToPremiumFlow");
        }
    }, 500);
    setTimeout(() => {
        if (localStorage.getItem("isAddShuttleTransferFlow") == "true") {
            showNotification("success", getTranslatedMessage("addShuttleTransferText"), getTranslatedMessage("bookingUpdateSuccess"), 3, "success");
            localStorage.removeItem("isAddShuttleTransferFlow");
        }
    }, 500);
    setTimeout(() => {
        if (localStorage.getItem("isManageSeatingFlow") == "true") {
            // Manage-seating's only confirmation used to fire at the Pay click
            // (Payment.tsx), which the MPGS redirect tears down before the
            // customer can read it — so a PAID seat change confirmed nothing.
            // Repeat it here, once the payment has actually completed. The
            // zero-amount path never reaches this helper, so no double toast.
            showNotification("success", getTranslatedMessage("manageSeating.payment.toaster.success"), "", 3, "success");
            localStorage.removeItem("isManageSeatingFlow");
        }
    }, 500);
    setTimeout(() => {
        if (localStorage.getItem("isChangeDateFlowForToast") == "true") {
            showNotification("success", getTranslatedMessage("bookingUpdateSuccess"), "", 3, "success");
            localStorage.removeItem("isChangeDateFlowForToast");
        }
    }, 500);
}

/**
 * The post-payment success sequence. THE single implementation, shared by every
 * path that observes a settled payment:
 *
 *  - legacy MPGS callback — /sessionCheckout answered 200 (verified + fulfilled
 *    client-side) or 409 (beef got there first)
 *  - beef redirect flow — /checkout/callback?status=FULFILLED, where the booking
 *    was already fulfilled server-side before the browser was redirected back
 *
 * Every one of these owes the customer the same things: submit the invoice they
 * requested, emit the confirmation analytics, consume the manage-booking flow
 * flags, and land them on their booking. Keeping that in one function is what
 * stops the paths drifting — the redirect flow initially re-implemented only
 * the storage-clearing slice and silently dropped the invoice and the analytics.
 *
 * Safe to call more than once for the same booking: the analytics emit dedupes
 * per bookingId via sessionStorage.
 */
export async function finalizePaymentSuccess(bookingId: string): Promise<void> {
    // Capture the flow flags SYNCHRONOUSLY — the awaits below give other paths a
    // chance to clear them, and this function clears CHANGE_DATE_FLOW itself.
    const confirmationFlags = readConfirmationFlowFlags();

    let bookingResponse = null;
    let bookingRef = "";
    if (bookingId) {
        try {
            const resp = await getBooking(bookingId, false);
            if (resp.status === "ok" && resp.answer) {
                bookingResponse = resp.answer;
                bookingRef = bookingResponse?.booking?.bookingCode || "";
            }
        } catch { }
    }

    const invoiceRequestPayload = JSON.parse(localStorage.getItem("invoiceRequestPayload") || "null");
    if (invoiceRequestPayload) {
        setTimeout(async () => {
            try {
                requestInvoiceSubmitAction.setParams({
                    bookingId: bookingId,
                    bookingRef: bookingRef,
                    formData: invoiceRequestPayload.formData,
                    userEmail: invoiceRequestPayload.userEmail,
                    afterPayment: true
                });
                await requestInvoiceSubmitAction.execute();
            } catch (e) {
            } finally {
                localStorage.removeItem("invoiceRequestPayload");
            }
        }, 0);
    }
    sessionStorage.removeItem("guestCheckoutBookingId");
    updateRecoilState("guestCheckoutBookingIdAtom", "")
    sessionStorage.removeItem("checkoutFromWithLogin");

    const isPassBooking = localStorage.getItem("isPassBooking") === "true";
    if (isPassBooking && bookingId) {
        redirectToPassBookingDetailPageAction.setParams({ bookingId });
        await redirectToPassBookingDetailPageAction.execute();

        setTimeout(() => {
            localStorage.removeItem("isPassBooking");
        })
        return;
    }

    //Remove Resume Search Object from Local Storage after payment Success
    localStorage.removeItem("resumeSearch");

    const isNormalBookingFlow = localStorage.getItem("isNormalBookingFlow") === "true";
    if (isNormalBookingFlow) {
        try {
            localStorage.setItem("normalBookingPaymentSuccess", "true");
        } catch { }
        localStorage.removeItem("isNormalBookingFlow");
    }
    // #7/#8/#9 — flags were captured up front, so this is correct wherever it runs.
    try {
        emitBookingConfirmationAnalytics(bookingId, bookingResponse, confirmationFlags);
    } catch (e) {
        console.error("booking confirmation analytics failed", e);
    }
    localStorage.removeItem(STORAGE_KEYS.CHANGE_DATE_FLOW);

    navigateToBookingDetails(bookingId);

    showPostPaymentFlowToasts();
}

/**
 * Post-settlement cleanup + navigation. Called from both the MPGS callback
 * (this file) and the 204 success branch of RedirectToPaymentGatewayAction
 * (zero-amount session / idempotency response). Both paths land the user
 * on /bookings/details/{id} with a fully finalized booking, so they share
 * the same atom-clearing + flow-close sequence.
 *
 * clearAllWhitelistedAtomsFromStorage wipes localStorage but not in-memory
 * jotai state; the explicit nulls below ensure the in-memory store also
 * resets (otherwise a stale "To Refund" row could surface between
 * settlement and the next manage-seating / booking-details entry whose
 * own clear runs).
 */
export const navigateToBookingDetails = (orderId: string) => {
    _eventBus.emit({
        eventName: "FLOW_CLOSE",
        data: `HomePageFlow`,
    });

    setTimeout(() => {
        _eventBus.emit({
            eventName: "ROUTE_CHANGE",
            data: `/bookings/details/${orderId}`,
        });
    }, 1000);
    clearAllWhitelistedAtomsFromStorage();
    updateRecoilState("appliedPassengerPassAtom", null);
    updateRecoilState("pendingRefundFulfillmentIdsAtom", null);
    updateRecoilState("refundOffersByFulfillmentIdAtom", null);
};

// Helper function to navigate to bookings page
export const navigateToBookings = () => {
    _eventBus.emit({
        eventName: "FLOW_CLOSE",
        data: `HomePageFlow`,
    });

    setTimeout(() => {
        _eventBus.emit({
            eventName: "ROUTE_CHANGE",
            data: `/bookings`,
        });
    }, 1000);
    clearAllWhitelistedAtomsFromStorage();
};

/** Flow flags that select which confirmation event (#7/#8/#9) fires. */
export interface ConfirmationFlowFlags {
    isChangeDate: boolean;
    isBookReturn: boolean;
}

/**
 * Read the flow flags that decide the confirmation branch. Callers that emit
 * AFTER the flags may be cleared (e.g. an async/fire-and-forget emit that runs
 * after navigateToBookingDetails wipes them) MUST capture these synchronously
 * up front and pass them into emitBookingConfirmationAnalytics.
 */
export function readConfirmationFlowFlags(): ConfirmationFlowFlags {
    return {
        isChangeDate: localStorage.getItem(STORAGE_KEYS.CHANGE_DATE_FLOW) === "true",
        isBookReturn: getRecoilStateValue("isBookReturnJourneyAtom") === true,
    };
}

/**
 * Emit exactly one booking-confirmation analytics event (#7/#8/#9). Branch
 * order is mutually exclusive: change-date -> book-return -> normal. Deduped per
 * bookingId via sessionStorage so a callback refresh can't double-count.
 *
 * Pass `flags` when the caller captured them synchronously before they could be
 * cleared; otherwise they are read live here (safe only when this runs BEFORE
 * the CHANGE_DATE_FLOW flag / book-return atom are cleared).
 */
export function emitBookingConfirmationAnalytics(
    bookingId: string,
    bookingResponse: unknown,
    flags?: ConfirmationFlowFlags,
): void {
    if (!bookingId) return;
    const dedupeKey = `analyticsConfirmed:${bookingId}`;
    try {
        if (sessionStorage.getItem(dedupeKey) === "1") return;
        sessionStorage.setItem(dedupeKey, "1");
    } catch {
        // sessionStorage unavailable — proceed without dedupe rather than skip.
    }

    const p = extractBookingParams(bookingResponse);
    const { isChangeDate, isBookReturn } = flags ?? readConfirmationFlowFlags();

    if (isChangeDate) {
        Analytics.changeDateConfirmation({
            bookingReference: p.bookingReference,
            changeFees: p.price,
            price: p.price,
            currency: p.currency,
        });
        return;
    }

    if (isBookReturn) {
        let original = "";
        try {
            original = localStorage.getItem("bookReturnOriginalBookingCode") ?? "";
            localStorage.removeItem("bookReturnOriginalBookingCode");
        } catch { }
        Analytics.roundTripPaymentConfirmation({
            bookingReference: original,
            newBookingReference: p.bookingReference,
            price: p.price,
            currency: p.currency,
            origin: p.origin,
            destination: p.destination,
            travelDate: p.travelDate,
            fare: p.ticketOption,
        });
        return;
    }

    Analytics.paymentConfirmation({
        bookingReference: p.bookingReference,
        paymentMethod: resolvePaymentMethod(),
        price: p.price,
        currency: p.currency,
        origin: p.origin,
        destination: p.destination,
        travelDate: p.travelDate,
        travelTime: p.travelTime,
        trainNumber: p.trainNumber,
        trainClass: p.trainClass,
        ticketOption: p.ticketOption,
        passengerCount: p.passengerCount,
    });
}

/**
 * Resolve the payment method for confirmation analytics. The paymentStateAtom is
 * cleared by the time the MPGS callback runs, so the method is stashed in
 * localStorage on the Pay click (see Payment.tsx) and read back here.
 */
function resolvePaymentMethod(): string {
    try {
        const stashed = localStorage.getItem("analyticsPaymentMethod");
        if (stashed) {
            localStorage.removeItem("analyticsPaymentMethod");
            return stashed;
        }
    } catch { }
    const paymentState = getRecoilStateValue("paymentStateAtom") as { method?: string } | null;
    return String(paymentState?.method ?? "");
}

export const paymentGatewayCheckoutAction = new PaymentGatewayCheckoutAction();
